/**
 * EM QUE PRATELEIRA UMA IMAGEM APARECE — E A GARANTIA DE QUE ELA APARECE EM ALGUMA.
 *
 * ── O PONTO (`T5.13`) ────────────────────────────────────────────────────────────────────────
 *
 * Ele, na letra: *"Figuras, figuras tem… Ó, que interessante. Ele… **entendeu como figuras algumas
 * coisas** lá de… do histórico (…) **Só algumas coisas, não foi tudo que foi transferido** (…) tem
 * que **transferir tudo**."*
 *
 * ── O QUE A LEITURA ACHOU, E NÃO É UMA TRANSFERÊNCIA QUEBRADA ────────────────────────────────
 *
 * Não existe "transferência" nenhuma neste produto: as imagens da conta sempre estiveram em
 * `users/{uid}/imgs`, cada uma com um campo `type`. O que existe são **três prateleiras que
 * PARTICIONAM essa lista**, e o `submenu.tsx` decidia assim:
 *
 *     .filter(([, info]) => info.type === selectedSubmenu)
 *
 * Igualdade exata. Então o `type` gravado decide tudo, e ele é escolhido em dois lugares, os dois
 * com padrão silencioso:
 *
 *   · `criar-comic.tsx` — se nenhuma prateleira estiver aberta, o padrão é `"scenarios"`;
 *   · `image-history.tsx` — há um `<select>`, e o padrão dele é `"characters"`.
 *
 * Quem gera cinco desenhos sem mexer no seletor manda os cinco para Personagens, vai procurar em
 * Figuras e acha **algumas coisas** — as que salvou com a prateleira certa aberta. É a experiência
 * que ele descreve, e o dado nunca se perdeu: ele está na prateleira do lado.
 *
 * ── E O CASO PIOR, QUE ESTA FUNÇÃO EXISTE PARA MATAR ─────────────────────────────────────────
 *
 * Com igualdade exata, **uma imagem cujo `type` não seja um dos três não aparece em prateleira
 * nenhuma.** Some. E isso não é hipótese de laboratório:
 *
 *   · `saveImg(src, type: ElType)` aceita `ElType`, que tem CINCO valores — `textBubbles` e
 *     `rectangles` também passam pela assinatura, e nenhuma das três prateleiras os mostra;
 *   · documento antigo, gravado antes de o campo existir, não tem `type`;
 *   · e o `salvarNoAcervo` do roteirizador grava `type: "figures"` na mão, num objeto literal —
 *     uma letra trocada ali e a imagem vira invisível sem erro nenhum.
 *
 * "Tem que transferir tudo" é, lido ao pé da letra, o pedido de que **nada fique invisível**. Daí a
 * regra: o que não é de nenhuma prateleira conhecida cai em **Figuras**, que é a genérica — o texto
 * dela no `submenu.tsx` diz que figura é *"um objeto solto que entra na cena"*, e é a única das três
 * que não promete nada sobre o conteúdo.
 */

/**
 * ── A QUARTA PRATELEIRA, E POR QUE ELA APARECEU DEPOIS — `T8.9` ──────────────────────────────
 *
 * Ele, na letra: *"Figuras **também traz 'Gerar imagem' junto dos balões**."* E era consequência
 * direta da regra acima: um balão salvo na conta tem `type: "textBubbles"`, `textBubbles` não era
 * nenhuma das três, e a regra mandava o desconhecido para Figuras. Os balões iam para a prateleira
 * de objetos soltos, ao lado de um botão de gerar imagem que não tem nada com eles.
 *
 * O `PRATELEIRA_GENERICA` existe para o **desconhecido** — documento antigo sem campo, `type` com
 * uma letra trocada. `textBubbles` deixou de ser desconhecido no dia em que "Textos" passou a ser
 * uma prateleira de verdade: agora ele tem casa própria, e a genérica volta a servir só ao que não
 * tem nome.
 *
 * `rectangles` CONTINUA caindo em Figuras: quadro não é imagem de acervo — ele se insere pelo
 * "Inserir Quadro" e nunca foi prateleira. Se um `type: "rectangles"` aparecer numa imagem, é dado
 * errado, e dado errado tem de ficar **visível** em algum lugar.
 */
export type Prateleira = "characters" | "scenarios" | "figures" | "textBubbles";

/** A genérica. Ver o cabeçalho para por que é ela e não uma prateleira nova. */
export const PRATELEIRA_GENERICA: Prateleira = "figures";

const CONHECIDAS: Prateleira[] = ["characters", "scenarios", "figures", "textBubbles"];

/** É uma das três? */
export function ehPrateleira(v: unknown): v is Prateleira {
  return typeof v === "string" && (CONHECIDAS as string[]).includes(v);
}

/**
 * A prateleira de uma imagem, pelo `type` gravado.
 *
 * **Nunca devolve nada fora das três.** É o que garante que toda imagem da conta esteja em alguma
 * prateleira — que é o "tudo" do pedido.
 */
export function prateleiraDa(type: unknown): Prateleira {
  return ehPrateleira(type) ? type : PRATELEIRA_GENERICA;
}

/** Uma imagem do catálogo, no mínimo que esta decisão precisa. */
export interface ImagemDaConta {
  type?: unknown;
  createdAt?: unknown;
}

/**
 * As imagens de uma prateleira, na ordem em que foram criadas.
 *
 * Substitui o `filter` por igualdade exata do `submenu.tsx`. A ordenação era e continua sendo por
 * `createdAt`, com `0` para quem não tem — o que põe as antigas primeiro, e é o que já acontecia.
 */
export function daPrateleira<T extends ImagemDaConta>(
  imgs: Record<string, T>,
  prateleira: Prateleira
): [string, T][] {
  return Object.entries(imgs)
    .filter(([, info]) => prateleiraDa(info?.type) === prateleira)
    .sort((a, b) => Number(a[1]?.createdAt || 0) - Number(b[1]?.createdAt || 0));
}

/**
 * Quantas imagens da conta não estão em prateleira nenhuma.
 *
 * Existe para o TESTE poder cobrar zero, e não para a tela. É a definição executável de "transferiu
 * tudo": some as três prateleiras e tem que dar o total.
 */
export function quantasInvisiveis(imgs: Record<string, ImagemDaConta>): number {
  const nasPrateleiras = CONHECIDAS.reduce((t, p) => t + daPrateleira(imgs, p).length, 0);
  return Object.keys(imgs).length - nasPrateleiras;
}
